import { InfoIcon, TagChip, Tip } from '@st/theme'
import { match } from '@st/pattern'

export type TaxReturnCandidate = {
  id: string
  name: string
  email?: string
  spouseEmail?: string
  returnType: string
  taxYear: number
  sourceProviderId?: string
  disabledReason?: string
  status?: { label: string; color: 'green' | 'gray' }
}

export type TaxReturnCandidateFailure = {
  id: string
  returnType?: string
  taxYear?: number
  errorCode: 'unsupported_record' | 'empty_record' | 'internal_error'
  message: string
}

type EditableSelection = {
  status: 'editable'
  selectedCandidateIds: Set<string>
  allCandidatesSelected: boolean
  onToggleAll: () => void
  onToggleCandidate: (candidateId: string) => void
}

type HiddenSelection = {
  status: 'hidden'
}

export function TaxReturnSelectionTable({
  candidates,
  failures = [],
  selection
}: {
  candidates: TaxReturnCandidate[]
  failures?: TaxReturnCandidateFailure[]
  selection: EditableSelection | HiddenSelection
}) {
  return (
    <table className="min-w-full table-fixed divide-y divide-gray-300">
      <thead className="sticky top-0 z-10 bg-white">
        <tr className="h-9">
          <th scope="col" className="w-16 pl-8 text-left text-xs uppercase text-gray-600">
            {selection.status == 'editable' && (
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={selection.allCandidatesSelected}
                onChange={selection.onToggleAll}
              />
            )}
          </th>
          <th scope="col" className="pl-8 text-left text-xs uppercase text-gray-600">
            Name
          </th>
          <th scope="col" className="text-left text-xs uppercase text-gray-600">
            Email
          </th>
          <th scope="col" className="text-left text-xs uppercase text-gray-600">
            Spouse Email
          </th>
          <th scope="col" className="text-left text-xs uppercase text-gray-600">
            Return Type
          </th>
          <th scope="col" className="text-left text-xs uppercase text-gray-600">
            Tax Year
          </th>
          <th scope="col" className="text-left text-xs uppercase text-gray-600">
            Record ID
          </th>
          <th scope="col" className="pr-8 text-left text-xs uppercase text-gray-600">
            Status
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {failures.map((failure) => (
          <FailureRow key={failure.id} failure={failure} />
        ))}
        {candidates.map((candidate) => (
          <CandidateRow key={candidate.id} candidate={candidate} selection={selection} />
        ))}
      </tbody>
    </table>
  )
}

function CandidateRow({
  candidate,
  selection
}: {
  candidate: TaxReturnCandidate
  selection: EditableSelection | HiddenSelection
}) {
  return (
    <tr className="h-12">
      <td className="whitespace-nowrap pl-8">
        {selection.status == 'editable' && (
          <Tip placement="right" title={candidate.disabledReason}>
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled={candidate.disabledReason != null}
              checked={selection.selectedCandidateIds.has(candidate.id)}
              onChange={() => selection.onToggleCandidate(candidate.id)}
              aria-label={`Select ${candidate.name}`}
            />
          </Tip>
        )}
      </td>
      <td className="whitespace-nowrap pl-8 text-base font-semibold">{candidate.name}</td>
      <td className="whitespace-nowrap text-base text-gray-500">{candidate.email}</td>
      <td className="whitespace-nowrap text-base text-gray-500">{candidate.spouseEmail}</td>
      <td className="whitespace-nowrap text-base text-gray-500">{candidate.returnType}</td>
      <td className="whitespace-nowrap text-base text-gray-500">{candidate.taxYear}</td>
      <td className="whitespace-nowrap text-base text-gray-500">{candidate.sourceProviderId}</td>
      <td className="whitespace-nowrap pr-8 text-base text-gray-500">
        {candidate.status && (
          <TagChip className="w-fit" color={candidate.status.color}>{candidate.status.label}</TagChip>
        )}
      </td>
    </tr>
  )
}

function FailureRow({ failure }: { failure: TaxReturnCandidateFailure }) {
  const label = match(failure.errorCode)
    .with('unsupported_record', () => 'Unsupported')
    .with('empty_record', () => 'Empty Client Record')
    .with('internal_error', () => 'Unexpected Error')
    .exhaustive()

  return (
    <tr className="h-12">
      <td className="whitespace-nowrap pl-8">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          disabled={true}
          checked={false}
        />
      </td>
      <td className="whitespace-nowrap pl-8 text-base font-semibold">{failure.id}</td>
      <td></td>
      <td></td>
      <td className="whitespace-nowrap text-base text-gray-500">{failure.returnType}</td>
      <td className="whitespace-nowrap text-base text-gray-500">{failure.taxYear}</td>
      <td className="whitespace-nowrap text-base text-gray-500">{failure.id}</td>
      <td className="whitespace-nowrap pr-8 text-base text-gray-500">
        <Tip title={failure.message} placement="top">
          <TagChip className="w-fit" color="red">
            <div className="flex items-center gap-1">
              <InfoIcon className="size-2.5" />
              {label}
            </div>
          </TagChip>
        </Tip>
      </td>
    </tr>
  )
}
